if (!customElements.get('card-cart-quantity')) {
  class CardCartQuantity extends HTMLElement {
    static cartRequest = null;

    // Shared across every stepper on the page so a sync is a single /cart.js request.
    static fetchCart() {
      if (!CardCartQuantity.cartRequest) {
        CardCartQuantity.cartRequest = fetch(`${routes.cart_url}.js`)
          .then((response) => response.json())
          .catch(() => null)
          .finally(() => {
            setTimeout(() => (CardCartQuantity.cartRequest = null));
          });
      }
      return CardCartQuantity.cartRequest;
    }

    connectedCallback() {
      this.quantity = Number(this.dataset.quantity) || 0;
      this.maxByVariant = this.dataset.maxByVariant ? JSON.parse(this.dataset.maxByVariant) : null;
      this.stepper = this.querySelector('.card-cart-quantity__stepper');
      this.countElement = this.querySelector('[data-count]');
      this.errorElement = this.querySelector('.card-cart-quantity__error');
      this.plusButton = this.querySelector('button[name="plus"]');
      this.minusButton = this.querySelector('button[name="minus"]');

      // On the product page the selected variant lives in the product form and
      // changes without a reload; on cards it is fixed.
      this.variantInput = this.dataset.variantForm
        ? document.querySelector(`#${this.dataset.variantForm} input[name="id"]`)
        : null;
      this.onVariantChange = () => {
        this.hideError();
        this.quantity = this.cart ? this.quantityIn(this.cart) : 0;
        this.render();
      };
      this.variantInput?.addEventListener('change', this.onVariantChange);

      this.plusButton.addEventListener('click', this.onStep.bind(this, 1));
      this.minusButton.addEventListener('click', this.onStep.bind(this, -1));

      this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event.source === 'card-cart-quantity' && event.cartData?.items) {
          this.syncFromCart(event.cartData);
        } else {
          this.refresh();
        }
      });

      this.onPageShow = (event) => event.persisted && this.refresh();
      window.addEventListener('pageshow', this.onPageShow);

      this.render();
      // The server-rendered count can be stale if the page came from cache.
      this.refresh();
    }

    disconnectedCallback() {
      this.unsubscribe?.();
      this.variantInput?.removeEventListener('change', this.onVariantChange);
      window.removeEventListener('pageshow', this.onPageShow);
    }

    get variantId() {
      return Number(this.variantInput ? this.variantInput.value : this.dataset.variantId) || null;
    }

    get max() {
      if (this.maxByVariant) {
        const max = this.maxByVariant[this.variantId];
        return max === null || max === undefined ? Infinity : Math.max(max, 0);
      }
      return this.dataset.max ? Math.max(Number(this.dataset.max), 0) : Infinity;
    }

    get minQty() {
      return Math.max(Number(this.dataset.min) || 1, 1);
    }

    get stepQty() {
      return Math.max(Number(this.dataset.step) || 1, 1);
    }

    // Elements shown only while the variant is not in the cart.
    get addTargets() {
      if (!this.dataset.addTargets) return [this.querySelector('product-form')].filter(Boolean);
      const scope = this.closest('product-info') || document;
      return [...scope.querySelectorAll(`[data-cart-add-target="${this.dataset.addTargets}"]`)];
    }

    refresh() {
      return CardCartQuantity.fetchCart().then((cart) => cart && this.syncFromCart(cart));
    }

    quantityIn(cart, variantId = this.variantId) {
      return cart.items
        .filter((item) => item.variant_id === variantId)
        .reduce((total, item) => total + item.quantity, 0);
    }

    syncFromCart(cart) {
      this.cart = cart;
      if (this.pendingTimer || this.requestInFlight) return;
      this.quantity = this.quantityIn(cart);
      this.render();
    }

    onStep(delta, event) {
      // Keeps the cart notification open while the shopper keeps adjusting.
      event.stopPropagation();
      const step = this.stepQty;
      const min = this.minQty;
      if (this.quantity === 0 && delta > 0) {
        this.setQuantity(min);
        return;
      }
      const next = this.quantity + delta * step;
      if (next < min) {
        this.setQuantity(0);
        return;
      }
      this.setQuantity(next);
    }

    setQuantity(quantity) {
      if (!this.variantId) return;
      this.quantity = Math.min(Math.max(quantity, 0), this.max);
      this.pendingVariantId = this.variantId;
      this.hideError();
      this.render();

      clearTimeout(this.pendingTimer);
      this.pendingTimer = setTimeout(() => {
        this.pendingTimer = null;
        this.commit();
      }, 350);
    }

    commit() {
      if (this.requestInFlight) {
        this.commitAgain = true;
        return;
      }

      const variantId = this.pendingVariantId;
      const requestedQuantity = this.quantity;
      const notification = document.querySelector('cart-notification');
      const sections = ['cart-icon-bubble'];
      if (notification && requestedQuantity > 0) {
        notification.getSectionsToRender().forEach((section) => {
          if (!sections.includes(section.id)) sections.push(section.id);
        });
      }

      this.requestInFlight = true;
      this.classList.add('is-loading');

      fetch(routes.cart_change_url, {
        ...fetchConfig(),
        body: JSON.stringify({
          id: String(variantId),
          quantity: requestedQuantity,
          sections,
          sections_url: window.location.pathname,
        }),
      })
        .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
        .then(({ ok, data }) => {
          if (!ok || !data.items) throw new Error(data.description || data.message || window.cartStrings.error);

          this.requestInFlight = false;
          this.cart = data;
          if (this.commitAgain || this.pendingTimer) {
            this.commitAgain = false;
            if (!this.pendingTimer) this.commit();
            return;
          }

          this.quantity = this.quantityIn(data);
          this.render();
          const savedQuantity = this.quantityIn(data, variantId);
          if (savedQuantity < requestedQuantity) {
            this.showError(window.cartStrings.quantityError.replace('[quantity]', savedQuantity));
          }

          this.renderCartIcon(data.sections);
          const line = data.items.find((item) => item.variant_id === variantId);
          if (notification && line && data.sections) {
            notification.renderContents({ key: line.key, sections: data.sections });
          }

          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'card-cart-quantity', cartData: data, variantId });
        })
        .catch((error) => {
          this.requestInFlight = false;
          this.commitAgain = false;
          this.showError(error.message || window.cartStrings.error);
          this.refresh();
        })
        .finally(() => this.classList.remove('is-loading'));
    }

    renderCartIcon(sections) {
      const html = sections?.['cart-icon-bubble'];
      const target = document.getElementById('cart-icon-bubble');
      if (!html || !target) return;
      const source = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
      if (source) target.innerHTML = source.innerHTML;
    }

    render() {
      const inCart = this.quantity > 0;
      this.stepper.hidden = !inCart;
      this.addTargets.forEach((element) => element.toggleAttribute('hidden', inCart));
      this.countElement.textContent = this.quantity;
      this.plusButton.disabled = this.quantity >= this.max;
    }

    showError(message) {
      this.errorElement.textContent = message;
      this.errorElement.hidden = false;
    }

    hideError() {
      this.errorElement.hidden = true;
      this.errorElement.textContent = '';
    }
  }

  customElements.define('card-cart-quantity', CardCartQuantity);
}
